import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { DecisionFilterBar } from '@/pages/anatel/DecisionFilterBar';
import { DoctrinePanel } from '@/pages/anatel/DoctrinePanel';
import {
  fetchDecidingAuthorities,
  fetchDecisionAuthorityPosture,
  fetchDecisionDoctrine,
  fetchOutcomeCodes,
  searchDecisions,
} from '@/services/anatel';
import type {
  AnatelAuthorityLine,
  AnatelAuthorityPosture,
  AnswerOut,
  DecisionFilters,
  DecisionList,
} from '@/services/types';

const PAGE_SIZE = 50;

export function AnatelDecisionsPage() {
  const { t } = useTranslation(['anatel', 'common']);
  // `?q=` seeds the search so a drill-through link (from a company profile, say)
  // arrives ALREADY FILTERED. Without this the link would open the unfiltered
  // corpus while looking like it had scoped the list -- the reader would have no
  // way to tell.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<DecisionFilters>({
    query: searchParams.get('q') || null,
    limit: PAGE_SIZE,
    offset: 0,
  });

  const { data: outcomeCodes = [] } = useQuery({
    queryKey: ['anatel', 'outcome-codes'],
    queryFn: fetchOutcomeCodes,
    staleTime: Infinity,
  });

  const { data: authorities = [] } = useQuery({
    queryKey: ['anatel', 'deciding-authorities'],
    queryFn: fetchDecidingAuthorities,
    staleTime: Infinity,
  });

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['anatel', 'decisions', filters],
    queryFn: () => searchDecisions(filters),
    // Keep the previous page visible while the next loads, so paging does not
    // flash an empty table.
    placeholderData: keepPreviousData,
  });

  // The two panels are corpus-wide and independent of every filter, so they
  // load ONCE, on their own keys, and stay put while the list narrows -- the
  // same shape as the option lists above and as `CoveragePanel`. Inside the
  // list query they were re-requested on every keystroke of the search.
  const doctrine = useQuery({
    queryKey: ['anatel', 'decisions', 'doctrine'],
    queryFn: fetchDecisionDoctrine,
    staleTime: Infinity,
  });
  const posture = useQuery({
    queryKey: ['anatel', 'decisions', 'authority-posture'],
    queryFn: fetchDecisionAuthorityPosture,
    staleTime: Infinity,
  });

  const offset = filters.offset ?? 0;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:decisions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:decisions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:decisions.explainer" />

      <FieldGuide i18nKey="anatel:decisions.guide" level={2} />

      {data?.as_of ? <AsOfStrip asOf={data.as_of} /> : null}

      <div className="flex justify-end">
        <ExportButton path="/api/v1/anatel/export/decisions" body={filters} />
      </div>

      <DecisionFilterBar
        filters={filters}
        outcomeCodes={outcomeCodes}
        authorities={authorities}
        onChange={setFilters}
      />

      {/* The active citation filter is NOT a select -- the vocabulary is 500+
          grounds. The doctrine panel sets it, and it shows as a chip with a
          clear, like the consistency queue's. */}
      {filters.citation ? (
        <ActiveFilterChip
          label={t('anatel:decisions.doctrine.filterActive', {
            citation: filters.citation,
          })}
          clearLabel={t('anatel:decisions.doctrine.filterClear')}
          onClear={() =>
            setFilters((current) => ({ ...current, citation: null, offset: 0 }))
          }
        />
      ) : null}

      {filters.deciding_authority ? (
        <ActiveFilterChip
          label={t('anatel:decisions.authority.filterActive', {
            authority: filters.deciding_authority,
          })}
          clearLabel={t('anatel:decisions.authority.filterClear')}
          onClear={() =>
            setFilters((current) => ({ ...current, deciding_authority: null, offset: 0 }))
          }
        />
      ) : null}

      {doctrine.isPending ? (
        <SkeletonLoader rows={3} />
      ) : doctrine.error ? (
        <ErrorMessage message={doctrine.error.message} onRetry={() => void doctrine.refetch()} />
      ) : (
        <DoctrinePanel
          answer={doctrine.data.citation_lines}
          selected={filters.citation ?? null}
          onSelectCitation={(citation) =>
            setFilters((current) => ({ ...current, citation, offset: 0 }))
          }
          exportPath="/api/v1/anatel/export/citation-lines"
          i18nPrefix="decisions.doctrine"
          outcomeKeyPrefix="anatel:overview.outcomes"
        />
      )}

      {posture.isPending ? (
        <SkeletonLoader rows={3} />
      ) : posture.error ? (
        <ErrorMessage message={posture.error.message} onRetry={() => void posture.refetch()} />
      ) : (
        <AuthorityPosturePanel
          answer={posture.data.authority_posture}
          selected={filters.deciding_authority ?? null}
          onSelectAuthority={(authority) =>
            setFilters((current) => ({ ...current, deciding_authority: authority, offset: 0 }))
          }
        />
      )}

      <DecisionResults
        data={data}
        isPending={isPending}
        isFetching={isFetching}
        error={error}
        offset={offset}
        onRetry={() => void refetch()}
        onPage={(next) => setFilters((current) => ({ ...current, offset: next }))}
      />
    </div>
  );
}

function DecisionResults({
  data,
  isPending,
  isFetching,
  error,
  offset,
  onRetry,
  onPage,
}: {
  data: DecisionList | undefined;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
  offset: number;
  onRetry: () => void;
  onPage: (offset: number) => void;
}) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  if (isPending) {
    return <SkeletonLoader rows={8} />;
  }
  if (error || !data) {
    return <ErrorMessage message={error?.message} onRetry={onRetry} />;
  }
  if (data.items.length === 0) {
    return <EmptyState hint={t('anatel:decisions.emptyHint')} />;
  }

  return (
    <div className="space-y-3">
      {/* The matching total, not the returned count. Both come from one
          shared predicate in the repository, so this line cannot contradict
          the table below it. */}
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {t('anatel:decisions.showing', {
          from: number.format(offset + 1),
          to: number.format(offset + data.items.length),
          total: number.format(data.total),
        })}
        {isFetching ? ` · ${t('common:state.loading')}` : ''}
      </p>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('anatel:decisions.columns.process')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:decisions.columns.date')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:decisions.columns.entity')}</th>
              <th className="px-3 py-2 font-medium">{t('anatel:decisions.columns.outcome')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('anatel:decisions.columns.fine')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr
                key={`${item.process_key}-${item.decision_number ?? ''}`}
                className="border-t hover:bg-muted/30"
              >
                <td className="px-3 py-2">
                  {/* Detail is reached by CLICKING A ROW, never from the
                      menu. The link uses the digits-only key because the
                      canonical number carries slashes. */}
                  <Link
                    to={`/anatel/p/${item.process_key}`}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {item.process_number}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                  {item.reference_date ?? '—'}
                </td>
                <td className="max-w-xs truncate px-3 py-2 text-foreground">
                  {item.entity_name ?? (
                    <span className="italic text-muted-foreground">
                      {t('anatel:decisions.noEntity')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {item.outcome_codes.length === 0
                    ? t('anatel:overview.outcomes.UNCLASSIFIED')
                    : item.outcome_codes
                        .map((code) =>
                          t(`anatel:overview.outcomes.${code}`, { defaultValue: code }),
                        )
                        .join(' · ')}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
                  {/* Blank, never 0: in a money column a zero is a value. */}
                  {item.fine_brl === null ? '' : currency.format(item.fine_brl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onPage(Math.max(offset - PAGE_SIZE, 0))}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.previous')}
        </button>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= data.total}
          onClick={() => onPage(offset + PAGE_SIZE)}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.next')}
        </button>
      </div>
    </div>
  );
}

/** A filter set by a panel, not the bar -- shown so a narrowed list never
    reads as the whole corpus. */
function ActiveFilterChip({
  label,
  clearLabel,
  onClear,
}: {
  label: string;
  clearLabel: string;
  onClear: () => void;
}) {
  return (
    <p className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-foreground">
      <span>{label}</span>
      <button
        type="button"
        onClick={onClear}
        className="rounded border px-1.5 py-0.5 font-medium text-primary underline-offset-4 hover:underline"
      >
        {clearLabel}
      </button>
    </p>
  );
}

/**
 * The outcome record of PADO, organised by the office that decided.
 *
 * The disclosures are on the panel because each one turns a true number into
 * a wrong impression when absent:
 *
 * 1. Offices, not people. PADO records the deciding OFFICE, so a line
 *    describes how an office's record resolved and says nothing about any
 *    individual decision-maker.
 * 2. Alphabetical, never ranked. Ordering offices by an outcome share would
 *    be a leniency ranking whatever the caption said, and the mix each office
 *    decides differs enough that the ranking would mislead.
 * 3. Blanks are disclosed, not dropped: `without_authority` finals count in
 *    the corpus but appear on no line, and the caption says how many.
 */
function AuthorityPosturePanel({
  answer,
  selected,
  onSelectAuthority,
}: {
  answer: AnswerOut<AnatelAuthorityPosture>;
  selected: string | null;
  onSelectAuthority: (authority: string) => void;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="rounded-lg border bg-card p-4">
      <Answer answer={answer} help={t('anatel:decisions.authority.help')}>
        {(value) => (
          <div className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {t('anatel:decisions.authority.title')}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('anatel:decisions.authority.intro')}
              </p>
            </div>
            <ul className="space-y-2">
              {value.lines.map((line) => (
                <AuthorityLineRow
                  key={line.authority}
                  line={line}
                  selected={line.authority === selected}
                  onSelect={() => onSelectAuthority(line.authority)}
                />
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {t('anatel:decisions.authority.showing', {
                shown: number.format(value.lines.length),
                total: number.format(value.offices_total),
                blank: number.format(value.without_authority),
              })}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}

function AuthorityLineRow({
  line,
  selected,
  onSelect,
}: {
  line: AnatelAuthorityLine;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <li
      className={
        selected
          ? 'space-y-1.5 rounded border border-primary/60 bg-primary/5 p-2.5'
          : 'space-y-1.5 rounded border p-2.5'
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {/* The office is the drill-through: a count on this panel reaches the
            decisions it counts (no orphan figures). */}
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="text-left text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          {line.authority}
        </button>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t('anatel:decisions.authority.decisions', { count: line.decisions })}
        </span>
      </div>
      <ul className="flex flex-wrap gap-1">
        {line.outcomes.map((outcome) => (
          <li
            key={outcome.code}
            className="rounded border bg-muted/40 px-1.5 py-0.5 text-xs text-foreground"
          >
            {t(`anatel:overview.outcomes.${outcome.code}`, { defaultValue: outcome.code })}
            <span className="ml-1 tabular-nums text-muted-foreground">
              {number.format(outcome.count)} · {percent.format(outcome.share)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}
